import { supabase, isSupabaseAvailable, subscribeToTable } from './supabase.js'
import { checkSupabaseConnection, setIsOnline } from '../config/supabase.js'
import { getSession } from '../auth/auth.js'

// Re-exportar subscribeToTable para páginas usarem
export { subscribeToTable }

export function getEmpresaId() {
  const session = getSession()
  return session?.empresaId || null
}

// ============================================================================
// CACHE EM MEMÓRIA — evita re-fetch desnecessário ao navegar entre páginas
// TTL padrão: 60 segundos. Invalidação automática após CRUD.
// ============================================================================

const CACHE_TTL = 60_000 // 60 segundos

const _cache = {
  produtos: { data: null, ts: 0 },
  funcionarios: { data: null, ts: 0 },
  movimentacoes: { data: null, ts: 0 },
  comprasPendentes: { data: null, ts: 0 },
  historicoPedidos: { data: null, ts: 0 },
  usuarios: { data: null, ts: 0 }
}

function getCached(key) {
  const entry = _cache[key]
  if (entry && entry.data && (Date.now() - entry.ts) < CACHE_TTL) {
    return entry.data
  }
  return null
}

function setCache(key, data) {
  _cache[key] = { data, ts: Date.now() }
}

export function invalidateCache(key) {
  if (key) {
    if (_cache[key]) _cache[key] = { data: null, ts: 0 }
  } else {
    Object.keys(_cache).forEach(k => { _cache[k] = { data: null, ts: 0 } })
  }
}

// ============================================================================
// COLUNAS ESPECÍFICAS POR TABELA — reduz egress do Supabase
// ============================================================================

const COLS = {
  produtos: '*',
  funcionarios: 'id,nome,matricula,departamento,funcao,status,imagem,empresa_id,dados,criado_em,atualizado_em',
  movimentacoes: 'id,tipo,produto_id,produto_nome,quantidade,quantidade_devolvida,quantidade_manutencao,funcionario_id,funcionario_nome,usuario_id,usuario_nome,observacao,status,foto_entrega,assinatura_funcionario,data_devolucao,empresa_id,dados,criado_em,atualizado_em',
  comprasPendentes: 'id,numero,usuario_id,itens,data_envio,empresa_id,dados,criado_em',
  historicoPedidos: 'id,numero,usuario_id,itens,data_envio,finalizado_em,empresa_id,dados,criado_em',
  usuarios: 'id,nome,login,email,perfil,ativo,empresa_id,criado_em,atualizado_em'
}

// ============================================================================
// MODELO ONLINE-FIRST SIMPLES
// Supabase é a ÚNICA fonte da verdade
// Sem merge complexo, sem filas de sync, sem offline-first
// ============================================================================

// Função para converter snake_case para camelCase
// e promover campo dados{} ao nível raiz (sem sobrescrever itens)
export function snakeToCamel(obj) {
  if (obj === null || obj === undefined) return obj
  if (Array.isArray(obj)) return obj.map(snakeToCamel)
  if (typeof obj !== 'object') return obj

  const result = {}
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
    result[camelKey] = value
  }

  // Promover campos de dados{} para o nível raiz (sem sobrescrever campos já existentes)
  if (result.dados && typeof result.dados === 'object' && !Array.isArray(result.dados)) {
    for (const [k, v] of Object.entries(result.dados)) {
      if (result[k] === undefined || result[k] === null) {
        result[k] = v
      }
    }
  }

  // Mapear criadoEm para dataMovimentacao para compatibilidade com IndexedDB
  if (result.criadoEm && !result.dataMovimentacao) {
    result.dataMovimentacao = result.criadoEm
  }

  // Mapear campos específicos da tabela produtos para compatibilidade
  if (result.quantidadeMin !== undefined && !result.estoqueMinimo) {
    result.estoqueMinimo = result.quantidadeMin
  }
  if (result.dataValidade === undefined && result.data_validade) {
    result.dataValidade = result.data_validade
  }

  return result
}

// Função para converter camelCase para snake_case
function camelToSnake(obj) {
  if (obj === null || obj === undefined) return obj
  if (Array.isArray(obj)) return obj.map(camelToSnake)
  if (typeof obj !== 'object') return obj

  const result = {}
  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
    result[snakeKey] = value
  }
  return result
}

// Upload dataURL para Supabase Storage e retorna URL pública
async function uploadDataUrlToStorage(dataUrl, folder = 'uploads') {
  try {
    if (!dataUrl || typeof dataUrl !== 'string') return dataUrl
    if (dataUrl.startsWith('http://') || dataUrl.startsWith('https://')) return dataUrl
    if (!dataUrl.startsWith('data:')) return dataUrl
    const [header, base64] = dataUrl.split(',')
    const mimeMatch = header.match(/data:(.*);base64/)
    const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream'
    const ext = mime.split('/')[1] || 'bin'
    const fileName = `${folder}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`
    const buffer = Uint8Array.from(atob(base64), c => c.charCodeAt(0))
    const { error: upErr } = await supabase.storage.from('uploads').upload(fileName, buffer, {
      contentType: mime,
      upsert: false
    })
    if (upErr) throw upErr
    const { data: pub } = supabase.storage.from('uploads').getPublicUrl(fileName)
    return pub?.publicUrl || dataUrl
  } catch (e) {
    console.warn('[STORAGE] Falha ao subir arquivo:', e?.message)
    // Fallback: manter dataURL para garantir exibição imediata
    return dataUrl
  }
}

// ============================================================================
// FUNÇÕES DE FETCH (PULL DO SUPABASE)
// ============================================================================

export async function fetchAllData() {
  console.log('[ONLINE] Buscando todos os dados do Supabase...')
  
  const [usuarios, produtos, funcionarios, movimentacoes, comprasPendentes, historicoPedidos] = await Promise.all([
    fetchUsuarios(),
    fetchProdutos(),
    fetchFuncionarios(),
    fetchMovimentacoes(),
    fetchComprasPendentes(),
    fetchHistoricoPedidos()
  ])
  
  console.log('[ONLINE] Dados carregados:', {
    usuarios: usuarios.length,
    produtos: produtos.length,
    funcionarios: funcionarios.length,
    movimentacoes: movimentacoes.length,
    comprasPendentes: comprasPendentes.length,
    historicoPedidos: historicoPedidos.length
  })
  
  return { usuarios, produtos, funcionarios, movimentacoes, comprasPendentes, historicoPedidos }
}

export async function fetchUsuarios(force = false) {
  if (!force) { const cached = getCached('usuarios'); if (cached) return cached }
  console.log('[ONLINE] Buscando usuários do Supabase...')
  const { data, error } = await supabase.from('usuarios').select(COLS.usuarios).eq('empresa_id', getEmpresaId())
  if (error) throw error
  const result = data.map(snakeToCamel)
  setCache('usuarios', result)
  return result
}

export async function fetchProdutos(force = false) {
  if (!force) { const cached = getCached('produtos'); if (cached) return cached }
  console.log('[ONLINE] Buscando produtos do Supabase...')
  const { data, error } = await supabase.from('produtos').select(COLS.produtos).eq('empresa_id', getEmpresaId())
  if (error) throw error
  const result = data.map(snakeToCamel)
  setCache('produtos', result)
  return result
}

export async function fetchFuncionarios(force = false) {
  if (!force) { const cached = getCached('funcionarios'); if (cached) return cached }
  console.log('[ONLINE] Buscando funcionários do Supabase...')
  const { data, error } = await supabase.from('funcionarios').select(COLS.funcionarios).eq('empresa_id', getEmpresaId())
  if (error) throw error
  const result = data
    .map(snakeToCamel)
    .map(f => ({
      ...f,
      status: (f.status || 'ativo'),
      nome: f.nome || '(Sem nome)'
    }))
    .sort((a, b) => (a.nome || '').localeCompare(b.nome || ''))
  setCache('funcionarios', result)
  return result
}

export async function fetchMovimentacoes(force = false) {
  if (!force) { const cached = getCached('movimentacoes'); if (cached) return cached }
  console.log('[ONLINE] Buscando movimentações do Supabase...')
  const { data, error } = await supabase.from('movimentacoes').select(COLS.movimentacoes).eq('empresa_id', getEmpresaId())
  if (error) throw error
  const result = data.map(snakeToCamel)
  setCache('movimentacoes', result)
  return result
}

export async function fetchComprasPendentes(force = false) {
  if (!force) { const cached = getCached('comprasPendentes'); if (cached) return cached }
  console.log('[ONLINE] Buscando compras pendentes do Supabase...')
  const { data, error } = await supabase.from('compras_pendentes').select(COLS.comprasPendentes).eq('empresa_id', getEmpresaId())
  if (error) throw error
  
  const result = data.map(item => {
    const camelItem = snakeToCamel(item)
    camelItem.itens = Array.isArray(camelItem.itens) ? camelItem.itens : []
    return camelItem
  })
  setCache('comprasPendentes', result)
  return result
}

export async function fetchHistoricoPedidos(force = false) {
  if (!force) { const cached = getCached('historicoPedidos'); if (cached) return cached }
  console.log('[ONLINE] Buscando histórico de pedidos do Supabase...')
  const { data, error } = await supabase.from('historico_pedidos').select(COLS.historicoPedidos).eq('empresa_id', getEmpresaId())
  if (error) throw error
  
  const result = data.map(item => {
    const camelItem = snakeToCamel(item)
    camelItem.itens = Array.isArray(camelItem.itens) ? camelItem.itens : []
    return camelItem
  })
  setCache('historicoPedidos', result)
  return result
}

export async function fetchAuditLogs(limit = 200) {
  console.log('[ONLINE] Buscando audit logs do Supabase...')
  const empresaId = getEmpresaId()
  const { data, error } = await supabase
    .from('audit_logs')
    .select('*')
    .or(`empresa_id.eq.${empresaId},empresa_id.is.null`)
    .order('data_hora', { ascending: false })
    .limit(limit)
  if (error) throw error
  return data.map(snakeToCamel)
}

// ============================================================================
// CRUD DIRETO NO SUPABASE (SEM FILA, SEM SYNC POSTERIOR)
// ============================================================================

// Criar
export async function createRecord(table, data) {
  console.log(`[ONLINE] Criando registro em ${table}:`, data.id)
  
  const snakeData = camelToSnake(data)
  
  // Injetar empresa_id automaticamente se não estiver presente
  if (!snakeData.empresa_id) {
    snakeData.empresa_id = getEmpresaId()
  }
  
  // Proteger itens JSONB
  if (table === 'compras_pendentes' || table === 'historico_pedidos') {
    snakeData.itens = Array.isArray(snakeData.itens) ? snakeData.itens : []
  }
  
  const { data: result, error } = await supabase.from(table).insert(snakeData).select().single()
  if (error) throw error
  
  invalidateCache(table === 'compras_pendentes' ? 'comprasPendentes' : table === 'historico_pedidos' ? 'historicoPedidos' : table)
  console.log(`[ONLINE] Registro criado: ${result.id}`)
  return snakeToCamel(result)
}

// Atualizar
export async function updateRecord(table, id, data) {
  console.log(`[ONLINE] Atualizando registro em ${table}:`, id)
  
  const snakeData = camelToSnake(data)
  
  // Proteger itens JSONB - nunca sobrescrever com []
  if (table === 'compras_pendentes' || table === 'historico_pedidos') {
    if (!snakeData.itens || (Array.isArray(snakeData.itens) && snakeData.itens.length === 0)) {
      // Se itens está vazio ou undefined, não enviar no update
      delete snakeData.itens
    }
  }
  
  const { data: result, error } = await supabase.from(table).update(snakeData).eq('id', id).select().single()
  if (error) throw error
  
  invalidateCache(table === 'compras_pendentes' ? 'comprasPendentes' : table === 'historico_pedidos' ? 'historicoPedidos' : table)
  console.log(`[ONLINE] Registro atualizado: ${id}`)
  return snakeToCamel(result)
}

// Deletar
export async function deleteRecord(table, id) {
  console.log(`[ONLINE] Deletando registro em ${table}:`, id)
  
  const { error } = await supabase.from(table).delete().eq('id', id)
  if (error) throw error
  
  invalidateCache(table === 'compras_pendentes' ? 'comprasPendentes' : table === 'historico_pedidos' ? 'historicoPedidos' : table)
  console.log(`[ONLINE] Registro deletado: ${id}`)
  return true
}

// ============================================================================
// FUNÇÕES ESPECÍFICAS PARA PEDIDOS (COM PROTEÇÃO DE ITENS)
// ============================================================================

export async function createCompraPendente(dadosInput) {
  console.log('[ONLINE] Criando compra pendente:', dadosInput.numero)
  
  // Colunas fixas do novo schema + extras em dados JSONB
  const { id, numero, itens, usuarioId, dataEnvio, ...extras } = dadosInput
  const registro = {
    id: id || crypto.randomUUID(),
    numero: numero,
    usuario_id: usuarioId || null,
    itens: Array.isArray(itens) ? itens : [],
    data_envio: dataEnvio || Date.now(),
    empresa_id: getEmpresaId(),
    dados: extras
  }
  
  const { data, error } = await supabase.from('compras_pendentes').insert(registro).select().single()
  if (error) {
    console.error('[ONLINE] Erro ao criar compra pendente:', error)
    throw error
  }
  
  invalidateCache('comprasPendentes')
  console.log('[ONLINE] Compra pendente criada:', data.id)
  return snakeToCamel(data)
}

export async function updateCompraPendente(id, dadosInput) {
  console.log('[ONLINE] Atualizando compra pendente:', id)
  
  // Colunas válidas do schema: numero, usuario_id, itens, data_envio, dados
  const update = {}
  
  if (dadosInput.numero !== undefined) update.numero = dadosInput.numero
  if (dadosInput.usuarioId !== undefined) update.usuario_id = dadosInput.usuarioId
  if (dadosInput.dataEnvio !== undefined) update.data_envio = dadosInput.dataEnvio
  
  // CRÍTICO: NUNCA sobrescrever itens com []
  if (dadosInput.itens !== undefined && Array.isArray(dadosInput.itens) && dadosInput.itens.length > 0) {
    update.itens = dadosInput.itens
  }
  
  // Campos extras vão para dados JSONB (ex: status, observacao, etc)
  const { numero, usuarioId, dataEnvio, itens, id: _id, ...extras } = dadosInput
  if (Object.keys(extras).length > 0) {
    update.dados = extras
  }
  
  const { data, error } = await supabase.from('compras_pendentes').update(update).eq('id', id).select().single()
  if (error) throw error
  
  invalidateCache('comprasPendentes')
  console.log('[ONLINE] Compra pendente atualizada:', id)
  return snakeToCamel(data)
}

export async function deleteCompraPendente(id) {
  console.log('[ONLINE] Deletando compra pendente:', id)
  return deleteRecord('compras_pendentes', id)
}

export async function createHistoricoPedido(dadosInput) {
  console.log('[ONLINE] Criando histórico de pedido:', dadosInput.numero)
  
  // Colunas fixas do novo schema + extras em dados JSONB
  const { id, numero, itens, usuarioId, dataEnvio, finalizadoEm, ...extras } = dadosInput
  const registro = {
    id: id || crypto.randomUUID(),
    numero: numero,
    usuario_id: usuarioId || null,
    itens: Array.isArray(itens) ? itens : [],
    data_envio: dataEnvio || Date.now(),
    finalizado_em: finalizadoEm || Date.now(),
    empresa_id: getEmpresaId(),
    dados: extras
  }
  
  const { data, error } = await supabase.from('historico_pedidos').insert(registro).select().single()
  if (error) {
    console.error('[ONLINE] Erro ao criar histórico de pedido:', error)
    throw error
  }
  
  invalidateCache('historicoPedidos')
  console.log('[ONLINE] Histórico de pedido criado:', data.id)
  return snakeToCamel(data)
}

export async function moverCompraParaHistorico(pedidoId) {
  console.log('[ONLINE] Movendo compra para histórico:', pedidoId)

  // Buscar pedido direto do Supabase por ID (sem fetch-all)
  const { data: pedidoData, error: pedidoError } = await supabase
    .from('compras_pendentes').select(COLS.comprasPendentes).eq('id', pedidoId).single()
  if (pedidoError) {
    console.error('[ONLINE] Erro ao buscar pedido:', pedidoError)
    throw new Error('Pedido não encontrado.')
  }
  if (!pedidoData) {
    console.error('[ONLINE] Pedido não encontrado:', pedidoId)
    throw new Error('Pedido não encontrado.')
  }
  const pedido = snakeToCamel(pedidoData)
  pedido.itens = Array.isArray(pedido.itens) ? pedido.itens : []
  console.log('[ONLINE] Pedido encontrado:', pedido.numero, 'status:', pedido.status)

  // Colunas fixas do novo schema + extras em dados JSONB
  const { id: _id, numero, itens, usuarioId, dataEnvio, status, ...extras } = pedido
  
  // Extrair nome do usuário que confirmou/rejeitou dos itens
  const itemConfirmado = itens.find(i => i.status === 'concluido' || i.status === 'parcial')
  const itemRejeitado = itens.find(i => i.status === 'rejeitado')
  const usuarioConfirmacaoNome = itemConfirmado?.usuarioConfirmacaoNome || itemRejeitado?.usuarioRejeicaoNome || extras.usuarioConfirmacaoNome || null
  
  const historicoSnake = {
    id: crypto.randomUUID(),
    numero: numero,
    usuario_id: usuarioId || null,
    empresa_id: getEmpresaId(),
    itens: Array.isArray(itens) ? itens : [],
    data_envio: dataEnvio || Date.now(),
    finalizado_em: Date.now(),
    dados: { 
      status: status || 'concluido', 
      usuarioConfirmacaoNome: usuarioConfirmacaoNome,
      ...extras 
    }
  }
  console.log('[ONLINE] Inserindo no histórico:', historicoSnake)

  const { data, error } = await supabase.from('historico_pedidos').insert(historicoSnake).select().single()
  if (error) {
    console.error('[ONLINE] Erro ao criar histórico:', error)
    throw error
  }

  // Deletar da compras_pendentes
  await deleteRecord('compras_pendentes', pedidoId)

  invalidateCache('comprasPendentes')
  invalidateCache('historicoPedidos')
  console.log('[ONLINE] Compra movida para histórico:', data.id)
  return snakeToCamel(data)
}

// ============================================================================
// CRUD PRODUTOS
// ============================================================================

export async function createProduto(dados) {
  const { id, nome, descricao, categoria, unidade, quantidade, quantidadeMin,
          estoqueMinimo, localizacao, ca, reutilizavel, dataValidade, imagem,
          tipo, codigo, foto, status, ...extras } = dados
  const registro = {
    id: id || crypto.randomUUID(),
    nome: nome || null,
    descricao: descricao || null,
    categoria: categoria || tipo || null,
    unidade: unidade || 'unidade',
    quantidade: Number(quantidade ?? 0),
    quantidade_min: Number(quantidadeMin ?? estoqueMinimo ?? 0),
    localizacao: localizacao || null,
    ca: ca || null,
    reutilizavel: reutilizavel ?? false,
    data_validade: dataValidade || null,
    imagem: imagem || foto || null,
    empresa_id: getEmpresaId(),
    dados: { tipo, codigo, status: status || 'ativo', ...extras },
    criado_em: Date.now(),
    atualizado_em: Date.now()
  }
  const { data, error } = await supabase.from('produtos').insert(registro).select().single()
  if (error) throw error
  invalidateCache('produtos')
  return snakeToCamel(data)
}

export async function updateProduto(id, patch) {
  const update = {}
  if (patch.nome !== undefined) update.nome = patch.nome
  if (patch.descricao !== undefined) update.descricao = patch.descricao
  if (patch.categoria !== undefined || patch.tipo !== undefined) update.categoria = patch.categoria || patch.tipo
  if (patch.unidade !== undefined) update.unidade = patch.unidade
  if (patch.quantidade !== undefined) update.quantidade = Number(patch.quantidade)
  if (patch.quantidadeMin !== undefined || patch.estoqueMinimo !== undefined)
    update.quantidade_min = Number(patch.quantidadeMin ?? patch.estoqueMinimo ?? 0)
  if (patch.localizacao !== undefined) update.localizacao = patch.localizacao
  if (patch.ca !== undefined) update.ca = patch.ca
  if (patch.reutilizavel !== undefined) update.reutilizavel = patch.reutilizavel
  if (patch.dataValidade !== undefined) update.data_validade = patch.dataValidade
  if (patch.imagem !== undefined || patch.foto !== undefined) update.imagem = patch.imagem || patch.foto
  update.atualizado_em = Date.now()

  // Campos extras no JSONB — MERGE com dados existentes (não sobrescrever)
  const { nome, descricao, categoria, tipo, unidade, quantidade, quantidadeMin,
          estoqueMinimo, localizacao, ca, reutilizavel, dataValidade, imagem, foto, ...extras } = patch
  if (Object.keys(extras).length > 0) {
    // Buscar dados JSONB atuais para preservar campos existentes
    const { data: current } = await supabase.from('produtos').select('dados').eq('id', id).single()
    const currentDados = (current?.dados && typeof current.dados === 'object') ? current.dados : {}
    update.dados = { ...currentDados, ...extras }
  }

  const { data, error } = await supabase.from('produtos').update(update).eq('id', id).select().single()
  if (error) throw error
  invalidateCache('produtos')
  return snakeToCamel(data)
}

export async function deleteProduto(id) {
  return deleteRecord('produtos', id)
}

// ============================================================================
// CRUD FUNCIONÁRIOS
// ============================================================================

export async function createFuncionario(dados) {
  const { id, nome, matricula, funcao, departamento, status, imagem, ...extras } = dados
  const registro = {
    id: id || crypto.randomUUID(),
    nome: nome || null,
    matricula: matricula || null,
    departamento: departamento || null,
    funcao: funcao || null,
    status: status || 'ativo',
    imagem: imagem || null,
    empresa_id: getEmpresaId(),
    dados: extras,
    criado_em: Date.now(),
    atualizado_em: Date.now()
  }
  const { data, error } = await supabase.from('funcionarios').insert(registro).select().single()
  if (error) throw error
  invalidateCache('funcionarios')
  return snakeToCamel(data)
}

export async function updateFuncionario(id, patch) {
  const { nome, matricula, funcao, departamento, status, imagem, ...extras } = patch
  const update = { atualizado_em: Date.now() }
  if (nome !== undefined) update.nome = nome
  if (matricula !== undefined) update.matricula = matricula
  if (funcao !== undefined) update.funcao = funcao
  if (departamento !== undefined) update.departamento = departamento
  if (status !== undefined) update.status = status
  if (imagem !== undefined) update.imagem = imagem
  if (Object.keys(extras).length > 0) {
    const { data: current } = await supabase.from('funcionarios').select('dados').eq('id', id).single()
    const currentDados = (current?.dados && typeof current.dados === 'object') ? current.dados : {}
    update.dados = { ...currentDados, ...extras }
  }

  const { data, error } = await supabase.from('funcionarios').update(update).eq('id', id).select().single()
  if (error) throw error
  invalidateCache('funcionarios')
  return snakeToCamel(data)
}

export async function deleteFuncionario(id) {
  return deleteRecord('funcionarios', id)
}

// ============================================================================
// CRUD MOVIMENTAÇÕES
// ============================================================================

export async function createMovimentacao(dados) {
  const { id, tipo, produtoId, produtoNome, quantidade, quantidadeDevolvida, quantidadeManutencao, funcionarioId, funcionarioNome,
          usuarioId, usuarioNome, observacao, status, fotoEntrega, assinaturaFuncionario,
          dataDevolucao, ...extras } = dados
  const fotoUrl = await uploadDataUrlToStorage(fotoEntrega, 'entregas')
  const assinaturaUrl = await uploadDataUrlToStorage(assinaturaFuncionario, 'assinaturas')
  const registro = {
    id: id || crypto.randomUUID(),
    tipo: tipo || null,
    produto_id: produtoId || null,
    produto_nome: produtoNome || null,
    quantidade: Number(quantidade ?? 0),
    quantidade_devolvida: quantidadeDevolvida !== undefined ? Number(quantidadeDevolvida) : null,
    quantidade_manutencao: quantidadeManutencao !== undefined ? Number(quantidadeManutencao) : null,
    funcionario_id: funcionarioId || null,
    funcionario_nome: funcionarioNome || null,
    usuario_id: usuarioId || null,
    usuario_nome: usuarioNome || null,
    observacao: observacao || null,
    status: status || 'ativa',
    foto_entrega: fotoUrl || null,
    assinatura_funcionario: assinaturaUrl || null,
    data_devolucao: dataDevolucao || null,
    empresa_id: getEmpresaId(),
    dados: extras,
    criado_em: Date.now(),
    atualizado_em: Date.now()
  }
  const { data, error } = await supabase.from('movimentacoes').insert(registro).select().single()
  if (error) throw error
  invalidateCache('movimentacoes')
  return snakeToCamel(data)
}

export async function updateMovimentacao(id, patch) {
  const { tipo, produtoId, produtoNome, quantidade, quantidadeDevolvida, quantidadeManutencao, funcionarioId, funcionarioNome,
          usuarioId, usuarioNome, observacao, status, fotoEntrega, assinaturaFuncionario,
          dataDevolucao, ...extras } = patch
  const update = { atualizado_em: Date.now() }
  if (tipo !== undefined) update.tipo = tipo
  if (produtoId !== undefined) update.produto_id = produtoId
  if (produtoNome !== undefined) update.produto_nome = produtoNome
  if (quantidade !== undefined) update.quantidade = Number(quantidade)
  if (quantidadeDevolvida !== undefined) update.quantidade_devolvida = Number(quantidadeDevolvida)
  if (quantidadeManutencao !== undefined) update.quantidade_manutencao = Number(quantidadeManutencao)
  if (funcionarioId !== undefined) update.funcionario_id = funcionarioId
  if (funcionarioNome !== undefined) update.funcionario_nome = funcionarioNome
  if (usuarioId !== undefined) update.usuario_id = usuarioId
  if (usuarioNome !== undefined) update.usuario_nome = usuarioNome
  if (observacao !== undefined) update.observacao = observacao
  if (status !== undefined) update.status = status
  if (fotoEntrega !== undefined) update.foto_entrega = await uploadDataUrlToStorage(fotoEntrega, 'entregas')
  if (assinaturaFuncionario !== undefined) update.assinatura_funcionario = await uploadDataUrlToStorage(assinaturaFuncionario, 'assinaturas')
  if (dataDevolucao !== undefined) update.data_devolucao = dataDevolucao
  if (Object.keys(extras).length > 0) {
    const { data: current } = await supabase.from('movimentacoes').select('dados').eq('id', id).single()
    const currentDados = (current?.dados && typeof current.dados === 'object') ? current.dados : {}
    update.dados = { ...currentDados, ...extras }
  }

  const { data, error } = await supabase.from('movimentacoes').update(update).eq('id', id).select().single()
  if (error) throw error
  invalidateCache('movimentacoes')
  return snakeToCamel(data)
}

export async function deleteMovimentacao(id) {
  return deleteRecord('movimentacoes', id)
}

// ============================================================================
// FUNÇÃO PARA OBTER PRÓXIMO NÚMERO DE PEDIDO (USANDO SUPABASE)
// ============================================================================

export async function getNextOrderNumber() {
  const now = new Date()
  const dd = String(now.getDate()).padStart(2, '0')
  const mm = String(now.getMonth() + 1).padStart(2, '0')
  const yy = String(now.getFullYear()).slice(-2)
  const hh = String(now.getHours()).padStart(2, '0')
  const min = String(now.getMinutes()).padStart(2, '0')
  return `PED-${dd}${mm}${yy}-${hh}${min}`
}

// ============================================================================
// FUNÇÃO PARA FORÇAR ATUALIZAÇÃO (RECARRREGAR DO SUPABASE)
// ============================================================================

export async function forceRefresh() {
  console.log('[ONLINE] Forçando atualização de dados...')
  
  if (!isSupabaseAvailable()) {
    console.warn('[ONLINE] Supabase não disponível')
    return null
  }
  
  // Limpar todo o cache para forçar re-fetch
  invalidateCache()
  const dados = await fetchAllData()
  
  // Disparar evento para atualizar estado global
  window.dispatchEvent(new CustomEvent('data-refreshed', {
    detail: dados
  }))
  
  console.log('[ONLINE] Atualização concluída')
  return dados
}

// ============================================================================
// INICIALIZAÇÃO
// ============================================================================

export async function initSyncManager() {
  console.log('[ONLINE] Inicializando gerenciador online-first...')
  
  // Verificar conexão
  const connected = await checkSupabaseConnection()
  
  if (connected) {
    setIsOnline(true)
    console.log('[ONLINE] Supabase disponível, pronto para uso')
  } else {
    setIsOnline(false)
    console.log('[ONLINE] Supabase não disponível')
  }
  
  // Listener para eventos de atualização
  window.addEventListener('force-refresh', () => {
    console.log('[ONLINE] Forçando atualização via evento...')
    forceRefresh()
  })
  
  // Listener para mudança de conexão
  window.addEventListener('online', async () => {
    console.log('[ONLINE] Conexão restaurada, verificando Supabase...')
    const reconnected = await checkSupabaseConnection()
    if (reconnected) {
      setIsOnline(true)
    }
  })
  
  window.addEventListener('offline', () => {
    console.log('[ONLINE] Conexão perdida')
    setIsOnline(false)
  })
}

export default {
  initSyncManager,
  fetchAllData,
  fetchUsuarios,
  fetchProdutos,
  fetchFuncionarios,
  fetchMovimentacoes,
  fetchComprasPendentes,
  fetchHistoricoPedidos,
  fetchAuditLogs,
  createRecord,
  updateRecord,
  deleteRecord,
  createCompraPendente,
  updateCompraPendente,
  deleteCompraPendente,
  getNextOrderNumber,
  forceRefresh,
  invalidateCache,
  subscribeToTable
}
